<template>
  <basic-modal.container
    :id="$props.modalId"
    ref="loginModalRef"
    @close="onClose"
  >
    <basic-modal.step
      :id="current"
      :key="current"
      size="midi"
      :type="currentStep?.actions.type"
      :step-title="currentStep.titleLabel"
    >
      <basic-modal.content>
        <component
          :is="currentStep.component"
          ref="dynamicContentRef"
          v-bind="currentStep.props"
          @error="handleError"
          @submit="currentStep.submit"
          @[customEvent]="currentStep?.actions.custom?.callback"
        />
      </basic-modal.content>

      <basic-modal.footer
        :cancel-label="currentStep?.actions.label?.cancel"
        :primary-label="currentStep?.actions.label?.primary"
        :primary-track-as="currentStep?.actions.trackAs?.primary"
        :secondary-label="currentStep?.actions.label?.secondary"
        :secondary-track-as="currentStep?.actions.trackAs?.secondary"
        @click-primary="currentStep?.proceed"
        @click-secondary="currentStep?.back"
        @click-tertiary="loginModalRef?.close"
      />
    </basic-modal.step>
  </basic-modal.container>
</template>

<script setup>
  import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue'

  import { events$, loading$ } from '@shared/services'
  import { CookieUtil } from '@shared/utils/Cookie'

  import { ReactivityUtil } from '@shared/utils/Reactivity'

  import customerAPI from '@shared/api/customer'

  import basketStore from '@shared/store/basket'
  import partnerStore from '@shared/store/partner'

  import useApplication from '@shared/hooks/useApplication'
  import useAuth from '@shared/hooks/useAuth'
  import useContentsquare from '@shared/hooks/useContentsquare'
  import useHn from './steps/hn/useHn'
  import useI18n from '@shared/hooks/useI18n'

  import BasicModal from '@shared/components/Basic/Modal/index.js'

  import {
    CHANNEL,
    LOGIN_OPEN_MODAL,
    LOGIN_TYPE,
    LOGIN_STEP,
    LOGIN_STEP_CS_ID,
    LOGIN_ERROR_STATE,
    PACKAGE,
  } from '@shared/config/constants'
  import { EVENT_MODAL, EVENT_LOGIN } from '@shared/config/events'
  import { logWarning } from '@shared/helpers/Logger'

  // HOOKS
  const { getDvpUrl } = useApplication()
  const { loginExistingCustomer } = useAuth()
  const { triggerCSPageEvent } = useContentsquare()
  const { answers, hasCardError, question, verifyAnswer, verifyCardNumber, verifyPartnerNumber } = useHn()
  const { t, te, selectedLanguage } = useI18n()

  // INIT
  const props = defineProps({
    modalId: {
      type: String,
      required: true,
    },
  })

  // DATA
  const current = ref(LOGIN_STEP.TYPE_SELECTION)
  const dynamicContentRef = ref(null)
  const errorType = ref(LOGIN_ERROR_STATE.GENERAL)
  const isActive = ref(false)
  const localBasket = reactive({})
  const localChannel = ref(props.channel)
  const localEmail = ref(null)
  const localId = ref(null)
  const localMobile = ref(null)
  const loginModalRef = ref(null)
  const loginType = ref(null)
  const localPartnerNumber = ref(null)
  const localPersons = reactive([])
  const immigrationRouteName = ref(null)
  const platform = ref(null)
  const localUid = ref(null)
  const navigateToDvp = ref(false)

  // COMPUTED
  const headOfContract = computed(() => {
    if (loginType.value === LOGIN_TYPE.ADVISOR) {
      return partnerStore.partner.distributionPartnerInfo
    } else {
      const contractOwnerId = localBasket?.contractOwner?.personId
      return localBasket?.persons?.find(p => p.personId === contractOwnerId).personData
    }
  })

  const currentStep = computed(() => {
    return steps.value[current.value]
  })

  const customEvent = computed(() => {
    return currentStep.value?.actions.custom?.name
  })

  const steps = computed(() => {
    return {
      [LOGIN_STEP.TYPE_SELECTION]: {
        actions: {
          type: 'transactional',
          label: {
            primary: t('login.typeSelection.button.kupo'),
            secondary: t('login.typeSelection.button.hn'),
          },
          trackAs: {
            primary: 'login_kupo',
            secondary: 'login_hn',
          },
        },
        component: defineAsyncComponent(() => import('./steps/TypeSelection')),
        back: () => {
          // LOGIN VIA HN QUESTIONS
          loginType.value = LOGIN_TYPE.HN
          setCurrentStep(LOGIN_STEP.AUTH_HN_CUSTOMER_NUMBER)
        },
        proceed: () => {
          // LOGIN VIA KUPO
          const loginStorage = {
            componentId: localId.value,
            modalId: props.modalId,
            startingChannel: localChannel.value,
            step: LOGIN_STEP.SUCCESS,
          }

          CookieUtil.set({
            key: LOGIN_OPEN_MODAL,
            value: JSON.stringify(loginStorage),
            lifetime: 300,
          })

          const redirectUri =
            localChannel.value === CHANNEL.ENTRYWIDGET ? getDvpUrl(selectedLanguage.value) : window.location.href

          loginExistingCustomer(redirectUri, selectedLanguage.value)
        },
        props: {},
        titleLabel: t('login.typeSelection.title'),
      },

      [LOGIN_STEP.AUTH_HN_CUSTOMER_NUMBER]: {
        actions: { type: 'progress' },
        back: () => {
          setCurrentStep(LOGIN_STEP.TYPE_SELECTION)
        },
        component: defineAsyncComponent(() => import('./steps/hn/CustomerNumber')),
        submit: async ({ partnerNumber }) => {
          try {
            const nextStep = await verifyPartnerNumber(partnerNumber)
            setCurrentStep(nextStep)
          } catch (error) {
            handleError(error.message)
          }
        },
        proceed: () => {
          dynamicContentRef.value.customerNumberRef.submit()
        },
        props: {},
        titleLabel: t('login.authform.hn.customerNumber.title'),
      },

      [LOGIN_STEP.AUTH_HN_CARD_NUMBER]: {
        actions: { type: 'progress' },
        back: () => {
          setCurrentStep(LOGIN_STEP.AUTH_HN_CUSTOMER_NUMBER)
        },
        component: defineAsyncComponent(() => import('./steps/hn/CardNumber')),
        submit: async ({ cardNumber }) => {
          try {
            const nextStep = await verifyCardNumber(cardNumber)
            // There was a card entry error, let the user try again
            if (!nextStep) return

            setCurrentStep(nextStep)
          } catch (error) {
            handleError(error.message)
          }
        },
        proceed: () => {
          dynamicContentRef.value.cardNumberRef.submit()
        },
        props: { showError: hasCardError.value },
        titleLabel: t('login.authform.hn.cardNumber.title'),
      },

      [LOGIN_STEP.AUTH_HN_QUESTIONS]: {
        actions: { type: 'progress' },
        back: () => {
          setCurrentStep(LOGIN_STEP.AUTH_HN_CARD_NUMBER)
        },
        component: defineAsyncComponent(() => import('./steps/hn/Question')),
        submit: async ({ answerKey }) => {
          try {
            const next = await verifyAnswer(answerKey)
            // More questions, stay on current login step
            if (next === LOGIN_STEP.AUTH_HN_QUESTIONS) return

            localPartnerNumber.value = next.data.partnerNumber
            localUid.value = next.data.uid
            ReactivityUtil.reAssign(localBasket, next.data.basket)
            setCurrentStep(next.step)
          } catch (error) {
            handleError(error.message)
          }
        },
        proceed: () => {
          dynamicContentRef.value.questionRef.submit()
        },
        props: { answers, question },
        titleLabel: te(`login.authform.hn.questions.${question.position}.title`)
          ? t(`login.authform.hn.questions.${question.position}.title`)
          : '',
      },

      [LOGIN_STEP.SUCCESS]: {
        actions: {
          type: 'transactional',
          label: {
            primary: t('form.next'),
            secondary: t('form.cancel'),
          },
        },
        back: () => {
          loginModalRef.value.close()
        },
        component: defineAsyncComponent(() => import('./steps/success/Form')),
        proceed: () => {
          dynamicContentRef.value.successFormRef.submit()
        },
        props: {
          basket: localBasket,
          loginType: loginType.value,
        },
        submit: () => {
          if (!localBasket.email) return setCurrentStep(LOGIN_STEP.EMAIL)
          if (!localBasket.mobile) return setCurrentStep(LOGIN_STEP.MOBILE)
          setCurrentStep(LOGIN_STEP.PERSONS_SELECT)
        },
        titleLabel: t('login.success.title'),
      },

      [LOGIN_STEP.ADVISOR_SUCCESS]: {
        actions: {
          type: 'transactional',
          label: {
            primary: t('form.next'),
            secondary: t('form.cancel'),
          },
        },
        back: () => {
          loginModalRef.value.close()
        },
        component: defineAsyncComponent(() => import('./steps/advisorSuccess')),
        proceed: () => {
          dynamicContentRef.value.successFormRef.submit()
        },
        props: {
          basket: localBasket,
          loginType: LOGIN_TYPE.ADVISOR
        },
        submit: () => {
          setCurrentStep(LOGIN_STEP.PERSONS_SELECT)
        },
        titleLabel: t('login.success.title'),
      },

      [LOGIN_STEP.EMAIL]: {
        actions: {
          type: 'transactional',
          label: {
            primary: t('form.next'),
          },
        },
        back: () => {
          loginModalRef.value.close()
        },
        component: defineAsyncComponent(() => import('./steps/success/Email')),
        proceed: () => {
          dynamicContentRef.value.emailFormRef.submit()
        },
        submit: ({ email }) => {
          localEmail.value = email
          if (!localBasket.mobile) return setCurrentStep(LOGIN_STEP.MOBILE)

          setCurrentStep(LOGIN_STEP.PERSONS_SELECT)
        },
        titleLabel: t('login.success.email.title'),
      },

      [LOGIN_STEP.MOBILE]: {
        actions: {
          type: 'transactional',
          label: {
            primary: t('form.next'),
          },
        },
        back: () => {
          loginModalRef.value.close()
        },
        component: defineAsyncComponent(() => import('./steps/success/Mobile')),
        proceed: () => {
          dynamicContentRef.value.mobileFormRef.submit()
        },
        submit: ({ mobile }) => {
          localMobile.value = mobile
          setCurrentStep(LOGIN_STEP.TAN)
        },
        titleLabel: t('login.success.mobile.title'),
      },

      [LOGIN_STEP.TAN]: {
        actions: {
          type: 'transactional',
          label: {
            primary: t('form.next'),
          },
        },
        back: () => {
          loginModalRef.value.close()
        },
        component: defineAsyncComponent(() => import('./steps/success/Tan')),
        proceed: () => {
          dynamicContentRef.value.tanFormRef.submit()
        },
        props: {
          basketId: localBasket.basketId,
          mobile: localMobile.value,
        },
        submit: () => {
          setCurrentStep(LOGIN_STEP.PERSONS_SELECT)
        },
        titleLabel: t('entry.resume'),
      },

      [LOGIN_STEP.PERSONS_SELECT]: {
        actions: {
          custom: {
            name: 'switch',
            callback: () => {
              setCurrentStep(LOGIN_STEP.PERSONS_NEW)
            },
          },
          type: 'transactional',
          label: {
            primary: t('form.apply'),
            secondary: t('form.cancel'),
          },
        },
        back: () => {
          loginModalRef.value.close()
        },
        component: defineAsyncComponent(() => import('./steps/persons/SelectPersons')),
        proceed: () => {
          dynamicContentRef.value.selectPersonsFormRef.submit()
        },
        props: {
          basketPersons: localBasket?.persons,
          loginType: loginType.value,
        },
        submit: persons => {
          ReactivityUtil.reAssign(localPersons, persons)
          setCurrentStep(LOGIN_STEP.LOADING)
        },
        titleLabel: t('login.person.form.title'),
      },

      [LOGIN_STEP.PERSONS_NEW]: {
        actions: {
          custom: {
            name: 'out-of-bound-registration',
            callback: routeName => {
              immigrationRouteName.value = routeName
              setCurrentStep(LOGIN_STEP.IMMIGRANT)
            },
          },
          type: 'progress',
          label: {
            primary: t('form.apply'),
            secondary: t('form.back'),
          },
        },
        back: () => {
          setCurrentStep(LOGIN_STEP.PERSONS_SELECT)
        },
        component: defineAsyncComponent(() => import('./steps/persons/AddPersons')),
        proceed: () => {
          dynamicContentRef.value.addPersonsFormRef.submit()
        },
        props: {
          basketPersons: localBasket?.persons,
          channel: localChannel.value,
          loginType: loginType.value,
        },
        submit: addedPersons => {
          ReactivityUtil.reAssign(localPersons, addedPersons)
          setCurrentStep(LOGIN_STEP.LOADING)
        },
        titleLabel: t('login.person.addPersons.title'),
      },

      [LOGIN_STEP.IMMIGRANT]: {
        actions: {
          type: 'acknowledgement',
          label: {
            primary: t('form.back'),
          },
        },
        component: defineAsyncComponent(() => import('@shared/components/Person/Immigrant')),
        proceed: () => {
          setCurrentStep(LOGIN_STEP.PERSONS_NEW)
        },
        props: {
          isModal: true,
          routeName: immigrationRouteName.value,
        },
      },

      [LOGIN_STEP.LOADING]: {
        actions: {
          type: 'raw',
        },
        back: () => {},
        component: defineAsyncComponent(() => import('./steps/Loading')),
        proceed: () => {},
        props: {
          localBasket: prepareBasket(),
          loginType: loginType.value,
          partnerNumber: localPartnerNumber.value,
          platform: platform.value,
          uid: localUid.value,
        },
        submit: () => {
          setCurrentStep(LOGIN_STEP.WELCOME)
        },
        titleLabel: t('login.loading.title'),
      },

      [LOGIN_STEP.WELCOME]: {
        actions: {
          type: 'acknowledgement',
          label: {
            primary: t('form.next'),
          },
        },
        back: () => {},
        component: defineAsyncComponent(() => import('./steps/Welcome')),
        proceed: () => {
          loginModalRef.value.close()
          if (navigateToDvp.value) {
            navigateAfterSubmit()
          }
        },
        titleLabel: t(
          'login.welcome.title',
          {
            firstName: headOfContract.value?.firstName,
            lastName: headOfContract.value?.lastName,
          },
          PACKAGE.DVP
        ),
      },

      [LOGIN_STEP.ERROR_GENERAL]: {
        actions: {
          type: 'passive',
        },
        back: () => {
          loginModalRef.value.close()
        },
        component: defineAsyncComponent(() => import('./error/General')),
        proceed: () => {},
        props: {
          type: errorType.value?.toLowerCase(),
        },
        titleLabel: t(`login.error.${errorType.value?.toLowerCase()}.title`),
      },
    }
  })

  // METHODS
  function getDataFromCookie() {
    const loginStorage = CookieUtil.read(LOGIN_OPEN_MODAL)
    if (!loginStorage) return
    /*
   Remove open-login item from localStorage to only trigger modal once per login
   If there are multiple login-prompts on the screen, the componentId will make sure
   that the modal is opened from the correct prompt
   */
    let componentId, modalId, startingChannel
    try {
    ;({ componentId, modalId, startingChannel } = JSON.parse(loginStorage))
    } catch {
      logWarning('Invalid localStorage value for login, Expected object, but got: ', loginStorage)
    }

    return {
      componentId,
      modalId,
      startingChannel,
    }
  }

  function handleError(type) {
    errorType.value = type || LOGIN_ERROR_STATE.GENERAL
    setCurrentStep(LOGIN_STEP.ERROR_GENERAL)
  }

  function initModal({
    callerId,
    channel: __channel,
    modalId,
    navigateToDvp: __navigateToDvp,
    openAll,
    platform: __platform,
    step,
  }) {
    if (!isCorrectModalId(__channel, modalId) && !openAll) return

    if (callerId) localId.value = callerId
    if (__channel) localChannel.value = __channel
    if (__navigateToDvp) navigateToDvp.value = __navigateToDvp

    switch (step) {
      case LOGIN_STEP.SUCCESS:
        /*
          We expect users to already be logged in at this point :/
          but we have to set the login type manually and also load the family basket
          the same way we do after manually logging in with Kupo
        */
        platform.value = __platform
        initModalAfterKupoLogin()
        break

      case LOGIN_STEP.ADVISOR_SUCCESS:
        loginType.value = LOGIN_TYPE.ADVISOR
        ReactivityUtil.reAssign(localBasket, basketStore.basket)

        setCurrentStep(step, true)
        break

      default:
        setCurrentStep(step, true)
        break
    }

    loginModalRef.value.open()
    isActive.value = true
  }

  async function initModalAfterKupoLogin() {
    try {
      loading$.start()
      const passthrough = ['DVP_CAN_NOT_SUPPORT_EXISTING_CUSTOMER', 'DVP_CAN_NOT_SUPPORT_PARTNER_AS_EXISTING_CUSTOMER']
      const __basket = await customerAPI.getFamilyBasket({ platform: platform.value, passthrough })
      loginType.value = LOGIN_TYPE.KUPO
      ReactivityUtil.reAssign(localBasket, __basket)
      events$.emit(EVENT_LOGIN.AUTHENTICATED, __basket)

      setCurrentStep(LOGIN_STEP.SUCCESS)
      loading$.end()
    } catch (e) {
      loading$.failed()
      handleError(e.message)
    } finally {
      CookieUtil.delete(LOGIN_OPEN_MODAL)
    }
  }

  function isCorrectModalId(channel, modalId) {
    // As we navigate from the entry-widget (.com) directly into DVP after keycloak-login
    // we know that there's only one possible login modal on the persons screen
    // and the old modal-id from the entry-widget is no longer valid
    return channel === CHANNEL.ENTRYWIDGET ? true : modalId === props.modalId
  }

  function navigateAfterSubmit() {
    const baseUrl = getDvpUrl(selectedLanguage.value)
    window.open(baseUrl, '_self').focus()
  }

  function onClose() {
    current.value = LOGIN_STEP.TYPE_SELECTION
    loginType.value = null
    scrollToWidget()
    isActive.value = false
  }

  function prepareBasket() {
    const newBasket = { ...localBasket, channel: localChannel.value }
    newBasket.persons = localPersons
    if (localEmail.value) newBasket.email = localEmail.value
    if (localMobile.value) newBasket.mobile = localMobile.value
    // Overwrite lang with currently selected language
    newBasket.language = selectedLanguage.value.toUpperCase()
    return newBasket
  }

  /**
   * @note: is only used for the productWidget, as we don't scroll to the entryWidget (and is not in use in dvp)
   */
  function scrollToWidget() {
    const callerEl = document.getElementById(localId.value)
    if (callerEl) {
      callerEl.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  function setCurrentStep(step, isInitial = false) {
    current.value = step || LOGIN_STEP.TYPE_SELECTION
    if (!isInitial) {
      events$.emit(EVENT_MODAL.UPDATE, {
        modalName: `${props.modalId}--${current.value}`,
      })
    }
  }

  // WATCHER
  watch(
    () => loginModalRef.value?.isOpen,
    val => {
      if (val) {
        events$.emit(EVENT_LOGIN.OPENED, localChannel.value)
      }
    }
  )

  watchEffect(() => {
    if (isActive.value && LOGIN_STEP_CS_ID[current.value]) triggerCSPageEvent(LOGIN_STEP_CS_ID[current.value])
  })

  // LIFECYCLE HOOKS
  onBeforeUnmount(() => {
    events$.off(EVENT_LOGIN.OPEN, initModal)
  })

  onMounted(() => {
    const localStorageData = getDataFromCookie()
    if (localStorageData && isCorrectModalId(localStorageData.startingChannel, localStorageData.modalId)) {
      localId.value = localStorageData.componentId
      localChannel.value = localStorageData.startingChannel

      // TODO: Added a timeout because there's no handling of multiple loading-starts
      setTimeout(async () => {
        await initModalAfterKupoLogin()
        loginModalRef.value.open()
        isActive.value = true
      }, 500)
    }

    events$.on(EVENT_LOGIN.OPEN, initModal)
  })

  defineExpose({ isActive })
</script>

<style scoped></style>
